import { valueOf } from "@/modules/irohana-components/types/common";
import { Accept } from "react-dropzone";

export const FILE_STATUS = {
  IN_PROGRESS: 0,
  OK: 1,
  ERROR: 2,
  ERROR_MIN_SIZE: 3,
  ERROR_MAX_SIZE: 4,
  ERROR_ACCEPT_FILES: 5,
};

export type TFileStatus = valueOf<typeof FILE_STATUS>;

export const FILE_ERROR_MESSAGE = {
  [FILE_STATUS.ERROR]: "FILE is invalid",
  [FILE_STATUS.ERROR_MAX_SIZE]: "ファイルサイズは{{maxSize}}以内でお願いします。",
  [FILE_STATUS.ERROR_MIN_SIZE]: "lt {{minSize}}。",
  [FILE_STATUS.ERROR_ACCEPT_FILES]: "ファイル形式は{{extensions}}にてお願いします。",
};

export const MERGE_PDFS_ERROR_MESSAGE = "結合処理に失敗しました。\nしばらく時間をおいてから、再度ダウンロードをお願いいたします。";

export const FILE_UPLOAD_FOR = {
  INFORMATION_FILE: "INFORMATION_FILE",
  CHAT_FILE: "CHAT_FILE",
  CONVERT_REQUEST_FILE: "CONVERT_REQUEST_FILE",
  PAYROLL_FILES: "PAYROLL_FILES",
  PAYCHECK_FILE: "PAYCHECK_FILE",
  RSO_ACCOUNT_FILE: "RSO_ACCOUNT_FILE",
  EMPLOYEE_AVATAR_FILE: "EMPLOYEE_AVATAR_FILE",
  EMPLOYEE_AVATAR_FILE_RESIZE: "EMPLOYEE_AVATAR_FILE_RESIZE",
  EMPLOYEE_AVATAR_THUMBNAIL_FOR_FORM: "EMPLOYEE_AVATAR_THUMBNAIL_FOR_FORM",
  EMPLOYEE_AVATAR_THUMBNAIL_FOR_LIST: "EMPLOYEE_AVATAR_THUMBNAIL_FOR_LIST",
  EMPLOYEE_CONTRACT_CONDITION_FILE: "EMPLOYEE_CONTRACT_CONDITION_FILE",
  EMPLOYEE_CONTRACT_EMPLOYMENT_FILE: "EMPLOYEE_CONTRACT_EMPLOYMENT_FILE",
  EMPLOYEE_PASSPORT_FILE: "EMPLOYEE_PASSPORT_FILE",
  EMPLOYEE_RESIDENCE_CARD_BACK_FILE: "EMPLOYEE_RESIDENCE_CARD_BACK_FILE",
  EMPLOYEE_RESIDENCE_CARD_FRONT_FILE: "EMPLOYEE_RESIDENCE_CARD_FRONT_FILE",
  EMPLOYEE_CAREER_TRAINING_COMPLETION_FILE: "EMPLOYEE_CAREER_TRAINING_COMPLETION_FILE",
  EMPLOYEE_CAREER_SKILL_COMPLETION_FILE: "EMPLOYEE_CAREER_SKILL_COMPLETION_FILE",
  EMPLOYEE_CAREER_JP_COMPLETION_1_FILE: "EMPLOYEE_CAREER_JP_COMPLETION_1_FILE",
  EMPLOYEE_CAREER_JP_COMPLETION_2_FILE: "EMPLOYEE_CAREER_JP_COMPLETION_2_FILE",
  EMPLOYEE_CAREER_CONFIRM_GUIDE_FILE: "EMPLOYEE_CAREER_CONFIRM_GUIDE_FILE",
  EMPLOYEE_CAREER_CONFIRM_ORI_FILE: "EMPLOYEE_CAREER_CONFIRM_ORI_FILE",
  EMPLOYEE_CAREER_DEPEND_FAMILY_FILE: "EMPLOYEE_CAREER_DEPEND_FAMILY_FILE",
  EMPLOYEE_CAREER_DEPEND_REMIT_FILE: "EMPLOYEE_CAREER_DEPEND_REMIT_FILE",
  EMPLOYEE_CAREER_RESUME_FILE: "EMPLOYEE_CAREER_RESUME_FILE",
  EMPLOYEE_ATTACHMENT_FILE: "EMPLOYEE_ATTACHMENT_FILE",
  CONTACT_FILE: "CONTACT_FILE",
  ANY_TIME_FILES: "ANY_TIME_FILES",
  RENEW_VISA_FILES: "RENEW_VISA_FILES",
  COE_FILES: "COE_FILES",
  COE_PDF_FILES: "COE_PDF_FILES",
  REGULAR_DOCUMENT_FILES: "REGULAR_DOCUMENT_FILES",
  RETIREMENT_FILE: "RETIREMENT_FILE",
  REGISTERED_SUPPORT_ENTRUSTMENT_CONTRACT_FILE: "REGISTERED_SUPPORT_ENTRUSTMENT_CONTRACT_FILE",
  COMPANY_ACCOUNT_RAS_IMMI_FILE: "COMPANY_ACCOUNT_RAS_IMMI_FILE",
};

export type TFileUploadFor = valueOf<typeof FILE_UPLOAD_FOR>;

export const MIME_TYPES = {
  IMAGE: ["image/jpg", "image/jpeg", "image/png"],
  HEIC: ["image/heic", "image/heif"],
  CSV: ["text/csv"],
  PDF: ["application/pdf"],
  EXCEL: [
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.oasis.opendocument.spreadsheet",
    "text/csv",
  ],
  WORD: [
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.oasis.opendocument.text",
    "application/msword",
    "application/x-ole-storage",
  ],
  TXT: ["text/plain"],
  XML: ["application/xml", "text/xml"],
  JPEG: ["image/jpg", "image/jpeg"],
  ZIP: ["zip", "application/octet-stream", "application/zip", "application/x-zip", "application/x-zip-compressed"],
  PPT: ["application/vnd.ms-powerpoint", "application/vnd.openxmlformats-officedocument.presentationml.presentation"],
  RAR: ["application/vnd.rar", "application/x-rar-compressed", "application/octet-stream", "application/x-rar"],
  GIF: ["image/gif"],
};

export const UPLOAD_COE_EXTENSION = [".pdf", ".doc", ".docx", ".xls", ".xlsx"];
export const UPLOAD_COE_MIME_TYPES = [
  "application/pdf", //pdf
  "application/x-ole-storage", //docx
  "application/msword", //doc
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", //doc
  "application/vnd.ms-excel", //xls
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", //xlsx
];

export const UPLOAD_RENEW_VISA_EXTENSION = [".pdf", ".doc", ".docx", ".xls", ".xlsx"];
export const UPLOAD_RENEW_VISA_MIME_TYPES = [
  "application/pdf", //pdf
  "application/x-ole-storage", //docx
  "application/msword", //doc
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", //doc
  "application/vnd.ms-excel", //xls
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", //xlsx
];

export const UPLOAD_REGULAR_DOCUMENT_EXTENSION = [".pdf", ".doc", ".docx"];

export const ONLY_PDF_MIME_TYPES = [".pdf"];

export const EXTENSION_NAMES: Record<string, string> = {
  IMAGE: "Image",
  HEIC: "HEIC",
  PDF: "PDF",
  EXCEL: "Excel",
  WORD: "Word",
  TXT: "Txt",
  CSV: "CSV",
  XML: "XML",
  JPEG: "Jpeg",
  ZIP: "Zip",
  RAR: "Rar",
  PPT: "Ppt",
  GIF: "Gif",
};

export const EXTENSIONS = {
  IMAGE: [".jpg", ".jpeg", ".png"],
  PDF: [".pdf"],
  WORD: [".doc", ".docx"],
  ZIP: [".zip"],
  EXCEL: [".xlsx", ".csv"],
  XML: [".xml"],
  HEIC: [".heic"],
  TXT: [".txt"],
  CSV: [".csv"],
  JPEG: [".jpeg"],
};

export const RATIO_FILE_UPLOAD = {
  minRatio: 2 / 3,
  maxRatio: 7 / 8,
};

export const FILE_EXISTED = "true";

export const COMMON_MINE_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
  "application/x-ole-storage",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain",
  "image/jpg",
  "image/jpeg",
  "image/png",
  "image/gif",
  "application/vnd.rar",
  "application/x-rar-compressed",
  "application/x-rar",
  "multipart/x-rar",
  "application/zip",
  "application/x-zip",
  "application/x-zip-compressed",
];

export const COMMON_DROPZONE_ACCEPT: Accept = {
  "application/pdf": [".pdf"],
  "application/msword": [".doc"],
  "application/x-ole-storage": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
  "application/vnd.ms-excel": [".xls"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
  "text/csv": [".csv"],
  "application/vnd.ms-powerpoint": [".ppt"],
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": [".pptx"],
  "text/plain": [".txt"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/gif": [".gif"],
  "application/zip": [".zip"],
  "application/x-zip": [".zip"],
  "application/x-zip-compressed": [".zip"],
  "application/vnd.rar": [".rar"],
  "application/x-rar-compressed": [".rar"],
  "application/x-rar": [".rar"],
};

export const COMMON_FILE_TYPES_EXTENSION = [
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".csv",
  ".ppt",
  ".pptx",
  ".txt",
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".rar",
  ".zip",
];

export const ACCEPT_ERROR = (extension: string[]) => `アップロード可能なファイル形式は${extension.join("、")} です。`;
